/**
 * Utilidades para RUT chileno: normalización, dígito verificador (módulo 11) y mensajes de ayuda.
 */

const UNICODE_HYPHENS = /[‐‑‒–—−]/g;

/**
 * Texto de ayuda con los formatos más usados (toast / error-messages).
 */
export function mensajeFormatosRutChileno() {
  return (
    "Usa el dígito verificador separado con guión. Ejemplos válidos: 12345678-9 · 12.345.678-9 · 76.351.470-6. " +
    "El verificador puede ser un número o la letra K (mayúscula o minúscula). Sin espacios al inicio o al final."
  );
}

/**
 * Quita puntos y espacios, unifica guiones unicode y, si falta guión, asume que el último carácter es el DV.
 * @param {string|null|undefined} raw
 * @returns {string}
 */
export function normalizeRutChileno(raw) {
  if (raw == null) return "";
  let s = String(raw).trim().replace(/\./g, "").replace(/\s/g, "");
  s = s.replace(UNICODE_HYPHENS, "-");
  if (!s.includes("-") && s.length >= 2) {
    const last = s.slice(-1).toUpperCase();
    if (/^[0-9K]$/.test(last)) {
      s = `${s.slice(0, -1)}-${last}`;
    }
  }
  return s;
}

/**
 * Mismo algoritmo módulo 11 que suele usarse en el portal (cuerpo numérico sin DV).
 * @param {string} cuerpo solo dígitos
 * @returns {'0'|'1'|...|'9'|'k'|null}
 */
export function calcularDvRutChileno(cuerpo) {
  if (!cuerpo || !/^\d+$/.test(cuerpo)) return null;
  let T = parseInt(cuerpo, 10);
  if (!Number.isFinite(T) || T < 0) return null;
  let M = 0;
  let S = 1;
  for (; T; T = Math.floor(T / 10)) {
    S = (S + (T % 10) * (9 - (M++ % 6))) % 11;
  }
  return S ? String(S - 1) : "k";
}

/**
 * RUT canónico para mostrar o enviar: cuerpo + "-" + DV en mayúsculas (K).
 * @param {string} raw
 * @returns {string|null}
 */
export function rutChilenoFormateado(raw) {
  const det = validateRutChilenoConDetalle(raw);
  if (!det.valid || !det.cuerpo || !det.dvRecibido) return null;
  const dv = det.dvEsperado === "k" ? "K" : det.dvEsperado;
  return `${det.cuerpo}-${dv}`;
}

/**
 * @param {string|null|undefined} raw
 * @returns {boolean}
 */
export function isValidRutChileno(raw) {
  return validateRutChilenoConDetalle(raw).valid;
}

/**
 * @typedef {{ valid: true, cuerpo: string, normalized: string }} RutOk
 * @typedef {{ valid: false, reason: 'vacio'|'formato'|'dv', message: string }} RutErr
 * @param {string|null|undefined} raw
 * @returns {RutOk|RutErr}
 */
export function validateRutChilenoConDetalle(raw) {
  const n = normalizeRutChileno(raw);
  if (!n) {
    return {
      valid: false,
      reason: "vacio",
      message: "Ingresa un RUT. " + mensajeFormatosRutChileno(),
    };
  }
  const m = /^(\d{1,8})-([0-9kK])$/i.exec(n);
  if (!m) {
    return {
      valid: false,
      reason: "formato",
      message: "El RUT no tiene un formato reconocible. " + mensajeFormatosRutChileno(),
    };
  }
  const cuerpo = m[1];
  const dvRecibido = m[2].toLowerCase() === "k" ? "k" : m[2];
  const dvEsperado = calcularDvRutChileno(cuerpo);
  if (dvEsperado == null) {
    return {
      valid: false,
      reason: "formato",
      message: "El número del RUT no es válido. " + mensajeFormatosRutChileno(),
    };
  }
  if (dvEsperado !== dvRecibido) {
    return {
      valid: false,
      reason: "dv",
      message:
        `El dígito verificador no corresponde (debería ser ${dvEsperado === "k" ? "K" : dvEsperado}). ` +
        mensajeFormatosRutChileno(),
    };
  }
  const normalized = `${cuerpo}-${dvEsperado === "k" ? "K" : dvEsperado}`;
  return {
    valid: true,
    cuerpo,
    dvRecibido,
    dvEsperado,
    normalized,
  };
}
